'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from '@/i18n/navigation'
import { createClient } from '@/lib/supabase/client'
import { getVerifiedClaims, claimsToUserShape } from '@/lib/supabase/claims'

/**
 * Paths the gate never covers.
 *
 * `/support` is the support URL submitted to the Apple App Store and Google
 * Play. Store reviewers open it cold, and this gate is a full-screen overlay
 * whose decline button sends the visitor to google.com — so a reviewer could be
 * bounced off the site instead of reaching the support contact. The page itself
 * is only a signpost (email, FAQ, Discord); there is nothing on it to gate.
 *
 * `usePathname` from `@/i18n/navigation` is locale-aware and returns the
 * un-prefixed path, so `/es/support` matches this list too.
 */
const AGE_GATE_EXEMPT_PATHS = ['/support']

function isAgeGateExempt(pathname: string): boolean {
  return AGE_GATE_EXEMPT_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

export function AgeGate() {
  const [visible, setVisible] = useState(false)
  const pathname = usePathname()
  const dialogRef = useRef<HTMLDivElement>(null)
  const confirmButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Store-review / support surface: never gated (see AGE_GATE_EXEMPT_PATHS).
    if (isAgeGateExempt(pathname)) {
      return
    }

    // If cookie is already set, skip immediately
    if (getCookie('age_verified') === 'true') {
      return
    }

    const supabase = createClient()

    // Check if age gate is enabled in site settings
    ;supabase
      .from('site_settings')
      .select('value')
      .eq('key', 'age_gate_enabled')
      .single()
      .then(async ({ data: setting }) => {
        // If setting exists and is explicitly 'false', skip the gate entirely
        if (setting && setting.value === 'false') {
          return
        }

        // Check if user is logged in and has age_verified in profile
        const claims = await getVerifiedClaims(supabase)
        const user = claims ? claimsToUserShape(claims) : null
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('age_verified')
            .eq('id', user.id)
            .single()

          if (profile?.age_verified) {
            // User already verified — set cookie and skip gate
            setCookie('age_verified', 'true', 365)
            return
          }
        }
        // No cookie and no profile verification — show gate
        setVisible(true)
      })
    // Re-evaluate on client-side navigation so entering via an exempt path
    // (e.g. a reviewer landing on /support) and then browsing to a gated page
    // still gets gated, and vice-versa.
  }, [pathname])

  // Modal focus management (WCAG 2.4.3 / 4.1.2 + the W3C dialog pattern):
  // when the gate appears, move focus into it, trap Tab inside it, swallow
  // Escape (a blocking gate must not be dismissable without an explicit
  // choice), and lock background scroll. On unmount/close the listener and
  // the scroll lock are torn down.
  useEffect(() => {
    if (!visible) return

    const raf = requestAnimationFrame(() => confirmButtonRef.current?.focus())

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Not a dismissal path — only the two buttons close the gate.
        e.preventDefault()
        e.stopPropagation()
        return
      }
      if (e.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !dialog.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.body.style.overflow = prevOverflow
    }
  }, [visible])

  // Render-time guard as well: even if `visible` was set true on a prior page,
  // an exempt path must never show the overlay.
  if (isAgeGateExempt(pathname) || !visible) return null

  const handleConfirm = async () => {
    setCookie('age_verified', 'true', 365)

    // If logged in, save to profile
    const supabase = createClient()
    const claims = await getVerifiedClaims(supabase)
    const user = claims ? claimsToUserShape(claims) : null
    if (user) {
      await supabase
        .from('profiles')
        .update({ age_verified: true })
        .eq('id', user.id)
    }

    setVisible(false)

    // Gate dismissed — move focus to the now-revealed page content instead of
    // leaving it on the unmounted button (which drops focus to <body>).
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content')
      if (main) {
        main.setAttribute('tabindex', '-1')
        main.focus()
      }
    })
  }

  const handleExit = () => {
    window.location.href = 'https://www.google.com'
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      data-testid="age-gate"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        aria-describedby="age-gate-desc"
        className="mx-4 w-full max-w-md bg-white p-8 text-center shadow-xl"
      >
        <h2 id="age-gate-title" className="text-2xl font-bold text-gray-900">Before you begin</h2>
        <p id="age-gate-desc" className="mt-4 text-gray-600">
          This site teaches rope bondage as a safety and art practice. Please
          continue only if this kind of educational content is appropriate for
          you.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            ref={confirmButtonRef}
            onClick={handleConfirm}
            className="bg-brand-700 px-6 py-3 text-sm font-semibold text-white hover:bg-brand-800 transition-colors"
            data-testid="age-gate-confirm"
          >
            I am 18 or older
          </button>
          <button
            onClick={handleExit}
            className="border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="age-gate-exit"
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  )
}
